import { FieldNames } from '@/constants/FieldNames';
import CompAddressUtils from '@/models/comp/CompAddressUtils';
import ICompAddress, { newCompAddress } from '@/models/CompAddress';
import IFeedData, { newFeedData } from '@/models/comp/IFeedData';
import IMisMatchAddressChoice from '@/models/local-storage/IMisMatchAddressChoice';
import IPublicRecord from '@/models/public-records/IPublicRecord';
import PublicRecordsRequest from '@/models/requests/PublicRecordsRequest';
import PublicRecordsService from '@/services/3.6/PublicRecordsService';
import TheWebEnvConfigService from '@/services/TheWebEnvConfigService';
import ProductFeaturesUtils from '@/utils/ProductFeaturesUtils';
import utils from '@/utils/QSUtils';
import LocalStorageManager from '../LocalStorageManager';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import CompsManager from './CompsManager';

/**
 * The manager for Public Records. Gets public records from either public data or smart data platform.
 */
export default {
    /**
     * Fetch feed data using the PublicRecordsService. The source of data may be public data or
     * smart data platform  based on whether SDP is enabled (in the JWT).
     * @param address
     * @param isSubject
     * @param formCode
     * @returns
     */
    async fetch(address: ICompAddress, isSubject: boolean, formCode: string): Promise<IFeedData | null> {
        let feedData: IFeedData | null = null;

        // Check for SDP enabled.
        const smartDataEnabled = ProductFeaturesUtils.isSmartDataEnabled();
        feedData = await fetchFromPublicRecords(address, isSubject, formCode, smartDataEnabled);

        // Check address for mismatch
        const compAddress: ICompAddress = newCompAddress();
        compAddress.address1 = address.address1 || '';
        compAddress.unitNumber = address.unitNumber || '';
        compAddress.city = address.city || '';
        compAddress.state = address.state || '';
        compAddress.zipCode = address.zipCode || '';

        const feedDataAddress: ICompAddress = newCompAddress();

        if(isSubject) {
            feedDataAddress.address1 = feedData?.sourceData?.SUBPROPADDRESS || '';
            feedDataAddress.unitNumber = feedData?.sourceData?.SUBUNITNO || '';
            feedDataAddress.city = feedData?.sourceData?.SUBCITY || '';
            feedDataAddress.state = feedData?.sourceData?.SUBSTATE || '';
            feedDataAddress.zipCode = feedData?.sourceData?.SUBZIP || '';      
        } else {
            feedDataAddress.address1 = feedData?.sourceData?.AD_ADDR1 || '';
            feedDataAddress.unitNumber = feedData?.sourceData?.DB_UNITNO || '';
            feedDataAddress.city = feedData?.sourceData?.DB_CITY || '';
            feedDataAddress.state = feedData?.sourceData?.DB_STATE || '';
            feedDataAddress.zipCode = feedData?.sourceData?.DB_ZIP || '';
        }

        // If mismatch occurs then write in to local stroage for the session
        const address1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(compAddress);
        if (utils.isJsonDifferent(compAddress, feedDataAddress)) {
            if (!LocalStorageManager.hasMisMatchAddress(address1AndUnitNo)) {
                let usingClosestAddress = null;
                // If address from SDP is blank then usingClosestAddress is false to prevent mismatch modal from displaying
                if (feedDataAddress.address1 === '') {
                    usingClosestAddress = false;
                }

                const misMatchAddress: IMisMatchAddressChoice = {
                    compAddress: compAddress,
                    closestAddress: feedDataAddress,
                    usingClosestAddress: usingClosestAddress,
                };
                LocalStorageManager.saveMisMatchAddress(address1AndUnitNo, misMatchAddress);
            }
        } else if (LocalStorageManager.hasMisMatchAddress(address1AndUnitNo)) {
            // Remove a stale mismatch choice. Otherwise setActiveComp() can immediately
            // clear newly loaded PR data even though this response matches the comp.
            LocalStorageManager.removeMisMatchAddress(address1AndUnitNo);
        }

        return feedData;
    },
};

/**
 * Fetch feed data using the DataDiscrepancyService (not using the Smart Data Platform).
 * @param address
 * @param isSubject
 * @param formCode
 * @returns
 */
async function fetchFromPublicRecords(address: ICompAddress, isSubject: boolean, formCode: string, smartDataEnabled: boolean): Promise<IFeedData | null> {
    const webEnvConfig = await TheWebEnvConfigService.getInstance().getConfigByHost();
    const service = new PublicRecordsService(webEnvConfig);

    const request: PublicRecordsRequest = {
        address: address,
        formCode: formCode,
        isSubject: isSubject,
        smartDataEnabled: smartDataEnabled
    };

    const data = await service.getFromPublicData(request);
    const feedData = await CompsManager.transformSourceDataPayload(data, SourceDataInitials.PublicRecords);
    if (feedData && feedData.sourceData) {
        return feedData;
    }
    return null;
}

function isValidPublicRecord(publicRecord: IPublicRecord): boolean {
    if (publicRecord) {
        if (utils.isKeyInObject(publicRecord, FieldNames.Address1)) {
            const address1 = publicRecord[FieldNames.Address1];
            if (address1) {
                return true;
            }
        }
    }
    return false;
}
